/**
 * 登记退款弹窗（ADM-33，UI 方案 v0.7 原型对齐复刻）。
 *
 * 只读摘要 8 格（订单号/学生/已收合计/累计退款/实收净额/本次可退上限/经办人/订单状态）；
 * 录入：退款金额（元，placeholder 最多可退）、退款渠道（必填 radio 5 值）、
 * 渠道退款单号（非现金必填、现金可不填）、退款凭证附件（可选，STS 直传 refund-evidences/）、
 * 退款原因（必填，记入订单操作历史）；高风险提示条 + 确认勾选必勾。
 * 幂等键打开时生成 UUID、失败重试复用；提交产生动作级 span：action: billing.registerRefund
 * （属性白名单仅 orderId/幂等键）。
 */
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Descriptions, Form, Input, InputNumber, Radio, Space, Upload, message } from 'antd';
import { RollbackOutlined, UploadOutlined } from '@ant-design/icons';
import { DraggableModal } from '../../components/DraggableModal';
import {
  billingApi,
  type RefundChannel,
  type RegisterOrderRefundResult,
} from '../../api/billing.api';
import { ApiError } from '../../api/transport';
import { startAction } from '../../observability/otel';

/** 整数分转「x.yy 元」纯文本（摘要展示，与 MoneyDisplay 同算法）。 */
function yuanText(amountMinor: number): string {
  const sign = amountMinor < 0 ? '-' : '';
  const abs = Math.abs(amountMinor);
  return `${sign}${Math.floor(abs / 100)}.${(abs % 100).toString().padStart(2, '0')} 元`;
}

/** 渠道中文标签（对齐原型 paymentChannelLabel）。 */
const REFUND_CHANNEL_LABEL: Record<RefundChannel, string> = {
  WECHAT: '微信',
  ALIPAY: '支付宝',
  CASH: '现金',
  BANK_TRANSFER: '银行转账',
  OTHER: '其他',
};

const REFUND_CHANNELS: RefundChannel[] = ['WECHAT', 'ALIPAY', 'CASH', 'BANK_TRANSFER', 'OTHER'];

/** 业务拒绝信封错误码 → 中文提示（未登记码回退服务端文案）。 */
const REFUND_ERROR_MESSAGE_BY_CODE: Record<string, string> = {
  'billing.refund_exceeds_refundable': '退款金额超出剩余可退，请核对后调整金额',
  'billing.refund_amount_invalid': '退款金额必须为大于零的整数分',
  'billing.refund_idempotency_conflict': '退款请求重复（同幂等键携带了不同内容），请刷新后重试',
  'billing.refund_channel_ref_no_required': '非现金渠道必须填写渠道退款单号/凭证号',
  'billing.order_not_found': '订单不存在',
};

interface RefundFormValues {
  amount: number | null;
  channel: RefundChannel;
  channelRefNo?: string;
  reason: string;
}

export function RefundRecordDialog({
  orderId,
  orderNo,
  studentName,
  paidAmountMinor,
  netReceivedAmountMinor,
  refundedAmountMinor,
  orderStatusLabel,
  operatorName,
  open,
  onClose,
  onRefunded,
}: {
  orderId: string;
  orderNo: string;
  /** 学生姓名（摘要格）。 */
  studentName: string;
  /** 已收合计（Σ活跃节点已收，分）。 */
  paidAmountMinor: number;
  /** 实收净额（= 剩余可退，分，服务端派生）。 */
  netReceivedAmountMinor: number;
  /** 累计退款（分）。 */
  refundedAmountMinor: number;
  /** 订单状态中文标签（摘要格）。 */
  orderStatusLabel: string;
  /** 经办人（当前登录员工姓名，摘要格只读）。 */
  operatorName: string;
  open: boolean;
  onClose: () => void;
  /** 登记成功回调（父级刷新实收/退款记录/操作历史）。 */
  onRefunded: (result: RegisterOrderRefundResult) => void;
}) {
  const [form] = Form.useForm<RefundFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [evidenceFileKey, setEvidenceFileKey] = useState<string | null>(null);
  const [evidenceName, setEvidenceName] = useState<string | null>(null);
  const [evidenceUploading, setEvidenceUploading] = useState(false);
  /** 幂等键：打开时生成，失败重试复用（不重置），关闭后由下次打开覆盖。 */
  const idempotencyKeyRef = useRef('');
  const selectedChannel = Form.useWatch('channel', form) as RefundChannel | undefined;

  useEffect(() => {
    if (!open) return;
    idempotencyKeyRef.current = crypto.randomUUID();
    setErrorMessage(null);
    setConfirmed(false);
    setEvidenceFileKey(null);
    setEvidenceName(null);
    form.resetFields();
  }, [open, form]);

  /** 凭证上传：先签发短时凭证（服务端校验类型/大小并限定前缀）。 */
  async function handleEvidenceUpload(file: File): Promise<void> {
    setEvidenceUploading(true);
    try {
      const granted = await billingApi.requestRefundEvidenceUpload(orderId, file.name, file.size);
      setEvidenceFileKey(granted.objectKey);
      setEvidenceName(file.name);
      message.success('凭证已就绪，将随登记一并提交');
    } catch (err) {
      message.error(err instanceof Error ? err.message : '凭证签发失败，请稍后重试');
    } finally {
      setEvidenceUploading(false);
    }
  }

  async function handleSubmit(): Promise<void> {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    // 前端上限拦截（SC-04）：剩余可退 = 服务端实收净额；请求不发出
    const amountMinor = Math.round((values.amount ?? 0) * 100);
    if (amountMinor > netReceivedAmountMinor) {
      setErrorMessage(
        `退款金额超出剩余可退（剩余可退 ${yuanText(netReceivedAmountMinor)}），请调整金额`,
      );
      return;
    }
    // 非现金渠道缺单号：前端拦截（服务端领域守卫兜底）
    const channelRefNo = values.channelRefNo?.trim() || null;
    if (values.channel !== 'CASH' && !channelRefNo) {
      setErrorMessage('非现金渠道必须填写渠道退款单号/凭证号');
      return;
    }
    // 确认勾选必勾（原型：未勾选确认 → 不可提交）
    if (!confirmed) {
      setErrorMessage('请先勾选「我已核实线下渠道退款已完成，并确认本次登记金额无误」');
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    // 动作级 RUM（frontend-rum §1.2 P0 资金链路）：属性白名单仅 orderId/幂等键
    const span = startAction('action: billing.registerRefund', {
      orderId,
      idempotencyKey: idempotencyKeyRef.current,
    });
    try {
      const result = await billingApi.registerOrderRefund(orderId, {
        amountMinor,
        reason: values.reason,
        remark: null,
        idempotencyKey: idempotencyKeyRef.current,
        channel: values.channel,
        channelRefNo,
        evidenceFileKey,
      });
      message.success(
        `登记成功，订单实收（净额）已更新为 ${yuanText(result.netReceivedAmountMinor)}`,
      );
      span.end();
      onRefunded(result);
      onClose();
    } catch (err) {
      span.end();
      if (err instanceof ApiError) {
        setErrorMessage(REFUND_ERROR_MESSAGE_BY_CODE[err.code] ?? err.message);
      } else {
        setErrorMessage(err instanceof Error ? err.message : '提交失败，请稍后重试');
      }
      // 失败保留已填内容与幂等键，不 resetFields
    } finally {
      setSubmitting(false);
    }
  }

  const isCash = selectedChannel === 'CASH';

  return (
    <DraggableModal
      title={
        <Space>
          <RollbackOutlined />
          登记退款
        </Space>
      }
      open={open}
      onCancel={onClose}
      width={720}
      destroyOnHidden
      footer={
        <>
          <Button onClick={onClose} disabled={submitting}>
            取 消
          </Button>
          <Button
            type="primary"
            danger
            loading={submitting}
            onClick={() => void handleSubmit()}
            data-testid="btn-submit-order-refund"
          >
            登 记
          </Button>
        </>
      }
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="高风险操作：仅登记线下已完成的退款事实，不改变订单状态、应收节点与课时权益；登记后实收（净额）立即扣减且不可撤销"
        data-testid="refund-risk-alert"
      />
      <Descriptions
        size="small"
        column={2}
        bordered
        style={{ marginBottom: 16 }}
        data-testid="refund-amount-summary"
      >
        <Descriptions.Item label="订单号">{orderNo}</Descriptions.Item>
        <Descriptions.Item label="学生">{studentName}</Descriptions.Item>
        <Descriptions.Item label="已收合计">{yuanText(paidAmountMinor)}</Descriptions.Item>
        <Descriptions.Item label="累计退款">{yuanText(refundedAmountMinor)}</Descriptions.Item>
        <Descriptions.Item label="实收（净额）">
          <b>{yuanText(netReceivedAmountMinor)}</b>
        </Descriptions.Item>
        <Descriptions.Item label="本次可退上限">
          <b>{yuanText(netReceivedAmountMinor)}</b>
        </Descriptions.Item>
        <Descriptions.Item label="经办人">{operatorName}</Descriptions.Item>
        <Descriptions.Item label="订单状态">{orderStatusLabel}</Descriptions.Item>
      </Descriptions>
      {errorMessage && (
        <Alert
          type="error"
          showIcon
          message={errorMessage}
          style={{ marginBottom: 12 }}
          data-testid="refund-error-alert"
        />
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={{ channel: 'WECHAT' }}
        disabled={submitting}
      >
        <Form.Item
          name="amount"
          label="退款金额（元）"
          rules={[
            {
              validator: (_rule, value: number | null) => {
                if (value === null || value === undefined) {
                  return Promise.reject(new Error('请填写退款金额'));
                }
                if (value <= 0) {
                  return Promise.reject(new Error('退款金额必须大于零'));
                }
                if (Math.round(value * 100) / 100 !== value) {
                  return Promise.reject(new Error('退款金额最多两位小数'));
                }
                if (Math.round(value * 100) > netReceivedAmountMinor) {
                  return Promise.reject(
                    new Error(`退款金额超出剩余可退（本次最多可退 ${yuanText(netReceivedAmountMinor)}）`),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          {/* 元输入两位小数，提交时 ×100 转分（spec order-manual-refund-admin-ui SC-03） */}
          <InputNumberLike placeholder={`最多可退 ${yuanText(netReceivedAmountMinor)}`} />
        </Form.Item>
        <Form.Item name="channel" label="退款渠道" required>
          <Radio.Group data-testid="refund-channel-group">
            {REFUND_CHANNELS.map((channel) => (
              <Radio key={channel} value={channel}>
                {REFUND_CHANNEL_LABEL[channel]}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="channelRefNo"
          label={isCash ? '渠道退款单号/凭证号（现金可不填）' : '渠道退款单号/凭证号'}
          rules={
            isCash
              ? undefined
              : [{ required: true, whitespace: true, message: '非现金渠道必须填写渠道退款单号/凭证号' }]
          }
        >
          <Input placeholder="请输入渠道退款单号或凭证号" data-testid="input-refund-channel-ref-no" />
        </Form.Item>
        <Form.Item label="退款凭证附件（可选）">
          <Upload
            beforeUpload={(file) => {
              void handleEvidenceUpload(file);
              return false;
            }}
            maxCount={1}
            showUploadList={false}
            disabled={submitting || evidenceUploading}
          >
            <button
              type="button"
              style={{ border: '1px dashed #d9d9d9', borderRadius: 6, padding: '4px 12px', background: '#fff' }}
              disabled={submitting || evidenceUploading}
              data-testid="btn-upload-refund-evidence"
            >
              <UploadOutlined /> {evidenceUploading ? '签发中…' : '上传凭证'}
            </button>
          </Upload>
          {evidenceName && (
            <span style={{ marginLeft: 8, color: '#666' }} data-testid="refund-evidence-name">
              {evidenceName}
            </span>
          )}
        </Form.Item>
        <Form.Item
          name="reason"
          label="退款原因"
          rules={[
            { required: true, whitespace: true, message: '请填写退款原因' },
            { max: 255, message: '原因不超过 255 字符' },
          ]}
        >
          <Input.TextArea
            rows={2}
            maxLength={255}
            placeholder="请说明退款原因（将记入订单操作历史）"
            aria-label="退款理由"
            data-testid="input-refund-reason"
          />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            data-testid="refund-confirm-checkbox"
          >
            我已核实线下渠道退款已完成，并确认本次登记金额无误
          </Checkbox>
        </Form.Item>
      </Form>
    </DraggableModal>
  );
}

/** 金额输入（转发 Form.Item 的 value/onChange；校验层负责元转分）。 */
function InputNumberLike({
  placeholder,
  value,
  onChange,
}: {
  placeholder?: string;
  value?: number | string | null;
  onChange?: (value: number | string | null) => void;
}) {
  return (
    <InputNumber
      min={0.01}
      precision={2}
      style={{ width: '100%' }}
      placeholder={placeholder}
      aria-label="退款金额"
      data-testid="input-refund-amount"
      value={value}
      onChange={onChange}
    />
  );
}
